import { useState, useEffect } from "react";
import { ShoppingBag, MapPin, Tag, Plus, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ImageUpload from "@/components/ImageUpload";
import AIListingHelper from "@/components/AIListingHelper";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = "918142445677";
const CATEGORIES = ["Appliances","Furniture","Electronics","Books","Clothing","Sports","Kitchen","Other"];
const CONDITIONS = ["Like New","Excellent","Good","Fair"];

const MarketplacePage = () => {
  const [items, setItems] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title:"", category:"", price:"", condition:"Good", location:"", contact:"", description:"", image_url:"" });

  useEffect(() => { axios.get(`${API}/marketplace`).then(r => setItems(r.data)).catch(console.error); }, []);

  const filtered = items.filter(i => catFilter === "all" || i.category === catFilter);
  const getWA = (i) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hi, interested in "${i.title}" at ${i.location} for INR ${i.price} on Junction. Still available?`)}`;
  const condColors = { "Like New":"bg-green-50 text-green-700", "Excellent":"bg-blue-50 text-blue-700", "Good":"bg-amber-50 text-amber-700", "Fair":"bg-gray-50 text-gray-600" };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.price || !form.contact) { toast.error("Fill required fields"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/marketplace`, form);
      setItems(prev => [res.data, ...prev]);
      toast.success("Item listed!");
      setForm({ title:"", category:"", price:"", condition:"Good", location:"", contact:"", description:"", image_url:"" });
      setDialogOpen(false);
    } catch { toast.error("Failed."); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="bg-terracotta/5 border-b border-terracotta/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-indigo mb-1">Neighborhood Marketplace</h1>
          <p className="text-sm text-gray-500">Buy & sell household goods, appliances, furniture within your neighborhood</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        <div id="post" className="flex flex-wrap gap-3 mb-6 items-center">
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setCatFilter("all")} className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${catFilter==="all" ? "bg-indigo text-white" : "bg-white text-gray-500 border border-gray-200 hover:border-indigo"}`}>All</button>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCatFilter(c)} className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${catFilter===c ? "bg-indigo text-white" : "bg-white text-gray-500 border border-gray-200 hover:border-indigo"}`} data-testid={`cat-filter-${c.toLowerCase()}`}>{c}</button>
            ))}
          </div>
          <div className="ml-auto">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild><Button variant="outline" className="rounded-full px-4 text-xs border-terracotta text-terracotta hover:bg-terracotta hover:text-white h-9" data-testid="post-item-button"><Plus size={14} className="mr-1" />Sell an Item</Button></DialogTrigger>
              <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="marketplace-dialog">
                <DialogHeader><DialogTitle className="font-heading text-lg text-indigo">Sell an Item</DialogTitle><DialogDescription>List for neighbors to discover.</DialogDescription></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3 mt-3">
                  <Input placeholder="Item name *" value={form.title} onChange={e => setForm({...form, title:e.target.value})} className="rounded-xl" data-testid="item-title-input" />
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={form.category} onValueChange={v => setForm({...form, category:v})}><SelectTrigger className="rounded-xl" data-testid="item-category-select"><SelectValue placeholder="Category *" /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                    <Select value={form.condition} onValueChange={v => setForm({...form, condition:v})}><SelectTrigger className="rounded-xl" data-testid="item-condition-select"><SelectValue /></SelectTrigger><SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Price (INR) *" value={form.price} onChange={e => setForm({...form, price:e.target.value})} className="rounded-xl" data-testid="item-price-input" />
                    <Input placeholder="Location" value={form.location} onChange={e => setForm({...form, location:e.target.value})} className="rounded-xl" data-testid="item-location-input" />
                  </div>
                  <Input placeholder="Contact *" value={form.contact} onChange={e => setForm({...form, contact:e.target.value})} className="rounded-xl" data-testid="item-contact-input" />
                  <ImageUpload value={form.image_url} onChange={(v) => setForm({...form, image_url: v})} />
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Description</label>
                      <AIListingHelper listingType="marketplace item" details={{title: form.title, category: form.category, condition: form.condition, price: form.price}} onGenerated={(d) => setForm({...form, description: d})} />
                    </div>
                    <Textarea placeholder="Description (or click AI Write)" value={form.description} onChange={e => setForm({...form, description:e.target.value})} className="rounded-xl min-h-[60px]" data-testid="item-description-input" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-indigo text-white rounded-full" data-testid="item-submit-button">{submitting ? "Posting..." : "List Item"}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <p className="text-xs text-gray-400 mb-4">{filtered.length} items found</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="marketplace-section">
          {filtered.map(item => (
            <div key={item.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-md transition-shadow" data-testid={`marketplace-card-${item.id}`}>
              {item.image_url && <div className="h-32 overflow-hidden"><img src={item.image_url} alt={item.title} className="w-full h-full object-cover" /></div>}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <ShoppingBag size={14} className="text-terracotta" />
                  <Badge className={`rounded-full text-[11px] ${condColors[item.condition] || "bg-gray-50 text-gray-600"}`}>{item.condition}</Badge>
                </div>
                <h4 className="font-medium text-indigo text-sm mb-1">{item.title}</h4>
                {item.description && <p className="text-[11px] text-gray-400 mb-2 line-clamp-2">{item.description}</p>}
                <div className="flex items-center gap-3 text-[11px] text-gray-400 mb-3">
                  <span className="flex items-center gap-1"><Tag size={10} />{item.category}</span>
                  <span className="flex items-center gap-1"><MapPin size={10} />{item.location}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                  <span className="font-heading font-semibold text-indigo text-base">INR {item.price}</span>
                  <div className="flex gap-1">
                    <a href={`tel:${item.contact}`}><Button variant="outline" size="sm" className="rounded-lg text-[11px] px-2 h-7 border-gray-200 text-terracotta hover:bg-terracotta hover:text-white" data-testid={`item-call-${item.id}`}><Phone size={10} /></Button></a>
                    <a href={getWA(item)} target="_blank" rel="noopener noreferrer"><Button size="sm" className="rounded-lg text-[11px] px-2 h-7 bg-green-600 hover:bg-green-700 text-white" data-testid={`item-whatsapp-${item.id}`}><MessageCircle size={10} /></Button></a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-center py-16 text-gray-400 text-sm" data-testid="no-items-message">No items listed yet.</p>}
      </div>
      <Footer />
    </div>
  );
};

export default MarketplacePage;
